<template>
  <v-dialog fullscreen scrollable persistent :value="value">
    <v-card color="kbLight">
      <v-toolbar color="kbLight" flat max-height="64px">
        <span class="text-h5 kbDarkBlueText--text text-capitalize font-weight-medium">
          {{ method === 'edit' ? $t('general.editSequence') : $t('general.newSequence') }}
        </span>
        <v-spacer></v-spacer>
        <v-btn color="kbDarkBlueText" icon @click="toggleModal()">
          <v-icon>clear</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider/>

      <v-card-text v-if="loadingSequence">
        <LoadingComponent :message="$t('general.loadingSequence')" />
      </v-card-text>

      <v-card-text v-else>
        <v-form ref="form">
          <v-row>
            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text d-block text-h6 mb-1 font-weight-regular">
                {{ $t('general.name') }}
              </span>
              <v-text-field background-color="kbWhite"
                            color="kbDarkBlueText"
                            :label="$t('general.sequenceName')"
                            :rules="[rules.required]"
                            solo
                            v-model="sequence.name"/>
            </v-col>

            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text d-block text-h6 mb-1 font-weight-regular">
                {{ $t('general.setResponsible') }}
              </span>
              <v-select background-color="kbWhite"
                        color="kbDarkBlueText"
                        :items="administrators"
                        item-text="fullName"
                        item-value="userId"
                        :label="$t('general.pickAResponsible')"
                        :rules="[rules.required]"
                        solo
                        v-model="sequence.responsibleId" />
            </v-col>

            <v-col cols="12">
              <v-divider />
            </v-col>

            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text d-block text-h6 mb-1 font-weight-regular">
                {{ $t('general.pickStartingDateTimeAndTimeZone') }}
              </span>
              <v-row>
                <v-col cols="12" md="6" class="d-flex">
                  <v-menu left max-width="500px">
                    <template v-slot:activator="{ on }">
                      <div>
                        <span class="kbGrayText--text text-subtitle-1 font-weight-regular d-block" style="margin-bottom: 16px">
                          {{ $t('general.pickADate') }}
                        </span>
                        <div class="d-flex align-center"
                             style="cursor: pointer"
                             v-on="on">
                          <v-btn color="mainButtons"
                                 class="mr-2"
                                 dark
                                 depressed
                                 fab
                                 height="35px"
                                 width="35px">
                            <v-icon size="20px">mdi-calendar</v-icon>
                          </v-btn>

                          <span class="kbDarkBlueText--text text-h6 text-capitalize text-no-wrap">
                            {{ startAtDate | moment('MMMM Do YYYY') }}
                          </span>
                        </div>
                      </div>
                    </template>
                    <v-row no-gutters>
                      <v-col cols="12">
                        <v-date-picker
                          color="kbBlue"
                          full-width
                          :landscape="$vuetify.breakpoint.mdAndUp"
                          v-model="startAtDate"
                        />
                      </v-col>
                    </v-row>
                  </v-menu>

                  <div class="ml-9">
                    <span class="kbGrayText--text text-subtitle-1 font-weight-regular d-block mb-2">
                      {{ $t('general.pickHour') }}
                    </span>
                    <PickHour :hour="parseInt(startAtHour)"
                              :minute="parseInt(startAtMinute)"
                              @onHourChange="startAtHour = $event.text"
                              @onMinuteChange="startAtMinute = $event.text"/>
                  </div>

                  <div class="ml-9" style="min-width: 400px">
                    <span class="kbGrayText--text text-subtitle-1 font-weight-regular d-block mb-2">
                      {{ $t('general.pickTimeZone') }}
                    </span>
                    <PickTimeZone v-model="sequence.initTimeZone" />
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12">
              <v-divider />
            </v-col>

            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text d-block text-h6 mb-1 font-weight-regular">
                {{ $t('general.isSequenceActive') }}
              </span>
              <v-switch color="kbBlue"
                        :dark="$vuetify.theme.isDark"
                        v-model="sequence.active" />
            </v-col>

            <v-col cols="12">
              <v-divider />
            </v-col>

            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text d-block text-h6 mb-1 font-weight-regular">
                {{ $t('general.pickAScheduleForTheSequence') }}
              </span>
              <v-autocomplete background-color="kbWhite"
                              color="kbDarkBlueText"
                              hide-details
                              :items="schedules"
                              item-text="name"
                              item-value="id"
                              :label="$t('general.pickASchedule')"
                              :loading="loadingSchedules"
                              :rules="[rules.required]"
                              solo
                              v-model="sequence.serviceHoursId" />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions class="d-flex justify-end">
        <v-btn
          color="kbBlue"
          class="mr-2"
          outlined
          @click="toggleModal()">
          {{ $t('general.cancel') }}
        </v-btn>
        <v-btn
          color="kbBlue"
          dark
          :loading="loadingSubmit"
          @click="submit()">
          {{ method === 'edit' ? $t('general.edit') : $t('general.create') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import notifications from '@/libs/notifications'
import rules from '@/libs/rules'
import LoadingComponent from '@/components/ui/LoadingComponent'
import PickHour from '@/pages/App/Sequences/widgets/Scheduler/PickHour'
import PickTimeZone from '@/pages/App/Sequences/widgets/Scheduler/PickTimeZone'
export default {
  components: { LoadingComponent, PickHour, PickTimeZone },
  computed: {
    ...mapState({
      administrators: state => state.companies.administrators,
      schedules: state => state.sequences.schedules,
      selectedSequence: state => state.sequences.selectedSequence
    }),
    sequenceStartingFullDateAndHour () {
      return `${this.startAtDate}T${this.startAtHour}:${this.startAtMinute}:00`
    }
  },
  created () {
    this.getAdministrators()
    this.getSchedules()
  },
  data () {
    return {
      loadingSchedules: false,
      loadingSequence: false,
      loadingSubmit: false,
      rules,
      sequence: {
        name: '',
        active: false,
        initSequenceAt: '',
        initTimeZone: 'America/New_York',
        responsibleId: '',
        serviceHoursId: ''
      },
      startAtDate: this.$moment(new Date()).format('YYYY-MM-DD'),
      startAtHour: '00',
      startAtMinute: '00'
    }
  },
  methods: {
    async changeSequenceStatus (active) {
      !active ? await this.stopSequence() : await this.runSequence()
    },
    clearSequence () {
      this.$refs.form.reset()
      this.sequence = {
        name: '',
        active: false,
        initSequenceAt: '',
        initTimeZone: 'America/New_York',
        responsibleId: '',
        serviceHoursId: ''
      }
      this.startAtDate = this.$moment(new Date()).format('YYYY-MM-DD')
      this.startAtHour = '00'
      this.startAtMinute = '00'
    },
    async createSequence () {
      const isSequenceValid = this.validateSequence()
      if (isSequenceValid !== true) return notifications.error(isSequenceValid)
      try {
        this.loadingSubmit = true
        let { initSequenceAt, ...all } = this.sequence
        initSequenceAt = this.sequenceStartingFullDateAndHour
        const response = await this.$store.dispatch('createSequence', {
          appId: this.$store.getters.appUuid,
          data: { initSequenceAt, ...all }
        })
        this.loadingSubmit = false
        notifications.success(this.$t('general.sequenceSuccessfullyCreated'))
        this.goToCreatedSequencePage(response.id)
      } catch (e) {
        this.loadingSubmit = false
        notifications.error(e)
      }
    },
    getAdministrators () {
      this.$store.dispatch('getAdministrators')
    },
    async getSchedules () {
      try {
        this.loadingSchedules = true
        await this.$store.dispatch(
          'getSequencesSchedules',
          { appId: this.$store.getters.appUuid }
        )
        this.loadingSchedules = false
      } catch (e) {
        notifications.error(e)
        this.loadingSchedules = false
      }
    },
    async getSequence () {
      try {
        this.loadingSequence = true
        await this.$store.dispatch('getSequence',
          {
            appid: this.$store.getters.appUuid,
            sequenceId: this.selectedSequenceId
          }
        )
        this.loadingSequence = false
        this.setLocalSequence()
      } catch (e) {
        this.loadingSequence = false
        notifications.error(e)
      }
    },
    goToCreatedSequencePage (sequenceId) {
      this.$router.push(
        {
          name: 'Sequence Leads',
          params: {
            appid: this.$store.getters.appUuid,
            sequenceId
          }
        }
      )
    },
    async runSequence () {
      try {
        await this.$store.dispatch('runSequence', { sequenceId: this.selectedSequenceId })
      } catch (e) {
        notifications.error(e)
      }
    },
    setLocalSequence () {
      this.sequence = { ...this.selectedSequence }
      const splitDateAndTime = this.selectedSequence.initSequenceAt.split('T')
      this.startAtDate = splitDateAndTime[0]
      const fullTime = splitDateAndTime[1].split(':')
      this.startAtHour = fullTime[0]
      this.startAtMinute = fullTime[1]
    },
    async stopSequence () {
      try {
        await this.$store.dispatch('stopSequence', { sequenceId: this.selectedSequenceId })
      } catch (e) {
        notifications.error(e)
      }
    },
    submit () {
      this.method === 'edit' ? this.updateSequence() : this.createSequence()
    },
    toggleModal () {
      this.$emit('input', false)
      this.clearSequence()
    },
    async updateSequence () {
      const isSequenceValid = this.validateSequence()
      if (isSequenceValid !== true) return notifications.error(isSequenceValid)
      try {
        this.loadingSubmit = true
        let { active, initSequenceAt, ...all } = this.sequence
        initSequenceAt = this.sequenceStartingFullDateAndHour
        await this.$store.dispatch('updateSequence', {
          appId: this.$store.getters.appUuid,
          sequenceId: this.selectedSequenceId,
          data: { active: this.selectedSequence.active, initSequenceAt, ...all }
        })
        if (active !== this.selectedSequence.active) {
          await this.changeSequenceStatus(active)
        }
        this.loadingSubmit = false
        notifications.success(this.$t('general.sequenceSuccessfullyUpdated'))
        this.$emit('getSequence')
        this.$emit('getSequences')
        this.toggleModal()
      } catch (e) {
        this.loadingSubmit = false
        notifications.error(e)
      }
    },
    validateSequence () {
      if (!this.$refs.form.validate()) return this.$t('general.youMustFillAllFieldsCorrectly')
      if (!this.startAtDate) return this.$t('general.youMustPickADate')
      return true
    }
  },
  props: {
    method: {
      type: String,
      default: 'create'
    },
    selectedSequenceId: {
      type: String,
      default: ''
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    value (value) {
      if (value && this.method === 'edit') {
        this.getSequence()
      }
    }
  }
}
</script>
